import React, { useState } from 'react';
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import { FaTachometerAlt, FaUsers, FaFlag, FaDollarSign, FaAd, FaSignOutAlt, FaBars, FaTimes, FaUserShield, FaCamera } from 'react-icons/fa';
import { useAuth } from '../context/AuthContext';

const AdminLayout = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { logout, user } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/');
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  const menuItems = [
    {
      path: '/admin',
      icon: FaTachometerAlt,
      label: 'Dashboard',
      active: location.pathname === '/admin'
    },
    {
      path: '/admin/users',
      icon: FaUsers,
      label: 'Users',
      active: location.pathname === '/admin/users'
    },
    {
      path: '/admin/photos',
      icon: FaCamera,
      label: 'Photo Moderation',
      active: location.pathname === '/admin/photos'
    },
    {
      path: '/admin/reports',
      icon: FaFlag,
      label: 'Reports',
      active: location.pathname === '/admin/reports'
    },
    {
      path: '/admin/finance',
      icon: FaDollarSign,
      label: 'Finance',
      active: location.pathname === '/admin/finance'
    },
    {
      path: '/admin/ads',
      icon: FaAd,
      label: 'Ads',
      active: location.pathname === '/admin/ads'
    }
  ];

  // Add roles menu item for super admin
  if (user && user.role && user.role.name === 'admin') {
    menuItems.splice(1, 0, {
      path: '/admin/roles',
      icon: FaUserShield,
      label: 'Roles',
      active: location.pathname === '/admin/roles'
    });
  }

  return (
    <div className="min-h-screen bg-gray-100 flex">
      {/* Mobile sidebar backdrop */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-40 bg-black bg-opacity-50 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <div className={`fixed lg:static inset-y-0 left-0 z-50 w-64 sm:w-72 md:w-80 xl:w-72 2xl:w-80 bg-white shadow-lg transform ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'} transition-transform duration-300 ease-in-out lg:translate-x-0 lg:w-64 xl:w-72 2xl:w-80 flex-shrink-0`}>
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="flex items-center justify-center h-16 lg:h-20 xl:h-24 px-4 lg:px-5 xl:px-6 bg-gradient-to-r from-pink-500 to-purple-600">
            <h1 className="text-lg sm:text-xl lg:text-2xl xl:text-3xl font-bold text-white">MatchUs Admin</h1>
          </div>

          {/* Navigation */}
          <nav className="flex-1 px-3 sm:px-4 lg:px-5 xl:px-6 py-4 sm:py-6 lg:py-8 space-y-1 sm:space-y-2">
            {menuItems.map((item) => {
              const IconComponent = item.icon;
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`flex items-center px-3 sm:px-4 lg:px-5 xl:px-6 py-2 sm:py-3 lg:py-4 text-xs sm:text-sm lg:text-base font-medium rounded-lg transition-colors duration-200 ${
                    item.active
                      ? 'bg-pink-100 text-pink-700 border-r-4 border-pink-500 shadow-sm'
                      : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
                  }`}
                  onClick={() => setSidebarOpen(false)}
                >
                  <IconComponent className="w-4 h-4 sm:w-5 sm:h-5 lg:w-6 lg:h-6 mr-2 sm:mr-3 lg:mr-4 flex-shrink-0" />
                  <span className="truncate">{item.label}</span>
                </Link>
              );
            })}
          </nav>

          {/* Logout */}
          <div className="p-4 lg:p-5 xl:p-6 border-t">
            <button
              onClick={handleLogout}
              className="flex items-center w-full px-4 lg:px-5 xl:px-6 py-3 lg:py-4 text-sm lg:text-base font-medium text-gray-600 rounded-lg hover:bg-gray-100 hover:text-gray-900 transition-colors duration-200"
            >
              <FaSignOutAlt className="w-5 h-5 mr-3" />
              Logout
            </button>
          </div>
        </div>
      </div>

      {/* Main content */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Top bar */}
        <div className="sticky top-0 z-30 flex items-center justify-between h-14 sm:h-16 px-3 sm:px-4 lg:px-6 xl:px-8 2xl:px-10 bg-white border-b shadow-sm flex-shrink-0">
          <div className="flex items-center">
            <button
              onClick={() => setSidebarOpen(!sidebarOpen)}
              className="p-1.5 sm:p-2 text-gray-500 rounded-lg hover:bg-gray-100 lg:hidden mr-2"
            >
              {sidebarOpen ? <FaTimes className="w-4 h-4 sm:w-5 sm:h-5" /> : <FaBars className="w-4 h-4 sm:w-5 sm:h-5" />}
            </button>
            <div className="hidden sm:block">
              <h2 className="text-sm sm:text-base font-semibold text-gray-900">Admin Panel</h2>
            </div>
          </div>

          <div className="flex items-center space-x-2 sm:space-x-4">
            <div className="text-xs sm:text-sm text-gray-600 hidden sm:block">
              Welcome back, <span className="font-medium">{user?.role?.name || 'Admin'}</span>
            </div>
            <div className="text-xs text-gray-500 sm:hidden">
              {user?.role?.name || 'Admin'}
            </div>
          </div>
        </div>

        {/* Page content */}
        <main className="flex-1 overflow-auto p-3 sm:p-4 lg:p-6 xl:p-8 2xl:p-10">
          <Outlet />
        </main>
      </div>
    </div>
  );
};

export default AdminLayout;